import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { computedAsync } from '@vueuse/core'
import type { Locale } from 'date-fns'
import { setDefaultOptions } from 'date-fns'
import { ca, de, enGB, es } from 'date-fns/locale'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { getCurrencyIcon, index } from '@last/core'
import { useTracker } from '@last/core-ui/paprika'

import { getConfig, sendUpdateHardwarePreferences } from '@/api/config'
import { addDevice } from '@/api/device'
import { useCustomerDisplay } from '@/composables/useCustomerDisplay'
import i18n from '@/i18n'
import images from '@/images'
//import { AdyenIPPPlugin } from '@/integrations/adyen/AdyenIPPPlugin'
import CashMachine from '@/integrations/cashmachine/cashmachine'
import Dataphone from '@/integrations/dataphone/dataphone'
import Printers from '@/integrations/printer/printer'
import { getSerial } from '@/integrations/SerialPlugin'
import { useAuthStore } from '@/store/auth'
import { useTillStore } from '@/store/till'
import sync from '@/sync/service'
import type { Config, Language, Printer } from '@/types/config'
import type { DeviceConfig } from '@/types/device'
import UpdaterEngine from '@/updater'
import { isDeprecatedVersion, notifyDeprecatedVersions } from '@/utils/deprecated-versions'
import { getDeviceInfo } from '@/utils/device'

export const useConfigStore = defineStore(
  'config',
  () => {
    const tracker = useTracker()
    // @ts-expect-error Minimal default when offline
    const config = ref<Config>({
      employees: {
        selectionType: 'names' as const
      },
      virtualBrands: [],
      tills: {
        cash: []
      },
      featureToggles: {},
      locationCountryCode: 'ES',
      currencyCode: 'EUR',
      paymentMethods: [],
      paymentMethodsConfig: [],
      blindTill: false,
      billCopies: 1,
      locationName: '',
      enableKitchenOrders: true,
      printers: [],
      autoprintReceipts: false,
      autoprintKitchenOrders: false,
      autoprintBills: false,
      dataphoneConfigs: [],
      adyenDataphones: [],
      preparationMinutes: 0,
      showTakeAwayTabPopup: false,
      onlyMasterPrinting: false,
      beepers: []
    })

    const mute = ref<boolean>(false)

    const fontSize = ref<number>(16)

    const useSecondaryDisplay = ref<boolean>(false)

    const device = ref<DeviceConfig>({
      id: '',
      mode: 'master',
      platform: 'web',
      accessToken: '',
      isDataphone: false
    })
    const poiid = ref<string | null>(null)
    const demoMode = ref(false)

    const virtualBrands = computed(() => config.value.virtualBrands)

    const tills = computed(() => {
      return config.value.tills
    })

    const currencyIcon = computed(() => {
      return getCurrencyIcon(config.value?.currencyCode)
    })

    const printers = computed(() => {
      return index(config.value.printers, 'id') as Record<string, Printer>
    })

    const appVersion = computed(() => {
      return device.value.appVersion || 'web'
    })

    const featureToggles = computed(() => {
      return Object.keys(config.value.featureToggles)
    })

    const deviceIsDeprecated = computedAsync(async () => {
      const deviceInfo = await getDeviceInfo()
      return isDeprecatedVersion(deviceInfo, device.value.isDataphone)
    })

    const shipmentProviders = computed(() => config.value.shipmentProviders)

    const currentAdyenDataphone = computed(() => {
      return (
        config.value.adyenDataphones?.find(dataphone => dataphone.poiid === poiid.value) || null
      )
    })

    async function refreshConfig(inputConfig?: Config) {
      const auth = useAuthStore()
      if (!auth.isAuthenticated) return
      let newConfig = inputConfig
      if (!newConfig) {
        try {
          newConfig = await getConfig()
        } catch {
          newConfig = {
            ...config.value
          }
        }
      }

      UpdaterEngine.checkForUpdates()

      newConfig.virtualBrands.forEach(brand => {
        if (brand.imageId) {
          images.preloadLogo(brand.imageId)
        }
      })
      const language = newConfig.language || 'es'
      i18n.global.locale.value = language
      const locales: Record<Language, Locale> = {
        es: es,
        ca: ca,
        de: de,
        en: enGB
      }
      setDefaultOptions({
        locale: locales[language]
      })
      Printers.loadPrinters(newConfig.printers)
      config.value = newConfig
      refreshDeviceInfo()
    }

    async function refreshDeviceInfo() {
      let addedDevice: DeviceConfig | Partial<DeviceConfig> = {}
      const platform = Capacitor.getPlatform()
      if (platform !== 'web') {
        const deviceInfo = await getDeviceInfo()
        const auth = useAuthStore()
        try {
          addedDevice = await addDevice(deviceInfo)
          auth.setAccessToken(addedDevice.accessToken!)
          device.value = addedDevice as DeviceConfig
          tracker.hasFeatureFlag('disabled_update_notification').then(disabled => {
            if (!disabled) {
              notifyDeprecatedVersions(deviceInfo, addedDevice.isDataphone)
            }
          })
        } catch (e: any) {
          if (e.type === 'devices_limit') {
            await auth.logout()
          }
          if (e.code === 'INVALID_DEVICE') {
            await auth.logout()
          }
        }
      }

      if (useSecondaryDisplay.value) {
        const { showSecondaryDisplay } = useCustomerDisplay()
        showSecondaryDisplay()
      }

      const enabledTills = tills.value.cash
      const preferredTill = enabledTills?.find(till => till.id === addedDevice.preferredTill)
      const till = useTillStore()
      if (preferredTill) {
        till.setTill(preferredTill)
      } else if (enabledTills.length === 1) {
        till.setTill(enabledTills[0])
      } else if (enabledTills.length > 1) {
        till.updateSelectedTillIfNull(enabledTills[0])
      }

      try {
        const info = await Device.getInfo()
        if (info.model === 'Saturn1000F2') {
          poiid.value = await getSerial()
        }
      } catch {
        // no-op
      }
      CashMachine.init(config.value, addedDevice.preferredCashMachine)
      Dataphone.init(config.value, addedDevice.preferredDataphone)
      //const auth = useAuthStore()
      /* if (auth.accessToken) {
        const tracker = useTracker()
        AdyenIPPPlugin.init({ authToken: auth.accessToken, tracker })
      } */
    }

    watch(
      fontSize,
      value => {
        document.documentElement.style.setProperty('font-size', `${value}px`)
      },
      { immediate: true }
    )

    function updateShipmentProvider(shipmentProvider: {
      provider: string | null
      ownFleetEnabled: boolean
    }) {
      sync.record('updateShipmentProvider', shipmentProvider)
    }

    function updateManualShipment(manualShipment: boolean) {
      sync.record('updateManualShipment', manualShipment)
    }

    function activateDemoMode() {
      demoMode.value = true
    }

    async function updateHardwarePreferences(hardwarePreferences: any) {
      if ('preferredCashMachine' in hardwarePreferences)
        CashMachine.init(config.value, hardwarePreferences.preferredCashMachine)

      if ('preferredDataphone' in hardwarePreferences) {
        Dataphone.init(config.value, hardwarePreferences.preferredDataphone)
      }

      device.value = {
        ...device.value,
        ...hardwarePreferences
      }

      if (device.value.id) {
        await sendUpdateHardwarePreferences(device.value.id, hardwarePreferences)
      }
    }

    const isShipmentProvidersEnabled = computed<boolean>(() => {
      if (!shipmentProviders.value || !shipmentProviders.value.providers) return false

      return shipmentProviders.value.providers.some(provider => {
        return provider.config?.enabled
      })
    })

    return {
      printers,
      config,
      mute,
      fontSize,
      useSecondaryDisplay,
      device,
      demoMode,
      virtualBrands,
      tills,
      currencyIcon,
      appVersion,
      shipmentProviders,
      currentAdyenDataphone,
      isShipmentProvidersEnabled,
      featureToggles,
      deviceIsDeprecated,
      refreshConfig,
      updateShipmentProvider,
      updateManualShipment,
      activateDemoMode,
      updateHardwarePreferences
    }
  },
  {
    persist: true
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
}
